import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Phone, Mail, MapPin, Clock, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Gallery images
  const galleryImages = [
    {
      url: "https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/ewqqhgyi_Screenshot%202025-12-10%20at%2001.28.11.png",
      alt: "Massage Elysana - Bild 1"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/e20ret5p_Screenshot%202025-12-10%20at%2001.36.14.png",
      alt: "Massage Elysana - Bild 2"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/u6mh4y5u_Screenshot%202025-12-10%20at%2001.29.53.png",
      alt: "Massage Elysana - Bild 3"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/bqlvnl8b_Screenshot%202025-12-10%20at%2001.30.40.png",
      alt: "Massage Elysana - Bild 4"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/ki8fnp35_Screenshot%202025-12-10%20at%2001.30.57.png",
      alt: "Massage Elysana - Bild 5"
    }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success("Nachricht erfolgreich gesendet! Wir werden uns bald bei Ihnen melden.");
      setFormData({ name: "", email: "", phone: "", message: "" });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background grain-texture">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-4 z-50 mx-4 md:mx-8 mt-4"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 rounded-full border border-white/20 bg-white/70 backdrop-blur-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/up1s5d57_Screenshot_2025-12-10_at_01.58.41-removebg-preview.png"
                alt="Elysana Logo"
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-primary hover:text-accent transition-colors">Startseite</a>
              <a href="/all-services" className="text-primary hover:text-accent transition-colors">Alle Leistungen</a>
              <a href="/faq" className="text-primary hover:text-accent transition-colors">FAQ</a>
              <a href="/contact" className="text-accent font-medium">Kontakt</a>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Contact Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-primary mb-6">Kontaktieren Sie uns</h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Haben Sie Fragen zu unseren Dienstleistungen? Wir freuen uns, von Ihnen zu hören. Schreiben Sie uns einfach eine Nachricht, und wir antworten Ihnen schnellstmöglich.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <h2 className="text-3xl font-bold text-primary mb-6">Senden Sie uns eine Nachricht</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-primary mb-2 block">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ihr Name"
                    required
                    data-testid="contact-input-name"
                    className="bg-white/50 border-primary/20 focus:border-primary rounded-md"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-primary mb-2 block">E-Mail-Adresse *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="ihre@email.de"
                    required
                    data-testid="contact-input-email"
                    className="bg-white/50 border-primary/20 focus:border-primary rounded-md"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-primary mb-2 block">Telefonnummer</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+49 123 456 7890"
                    data-testid="contact-input-phone"
                    className="bg-white/50 border-primary/20 focus:border-primary rounded-md"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-primary mb-2 block">Nachricht *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Teilen Sie uns mit, wie wir Ihnen helfen k\u00f6nnen..."
                    required
                    rows={6}
                    data-testid="contact-input-message"
                    className="bg-white/50 border-primary/20 focus:border-primary rounded-md resize-none"
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  <Send size={20} />
                  {isSubmitting ? "Wird gesendet..." : "Nachricht senden"}
                </motion.button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold text-primary mb-6">Kontaktinformationen</h2>
                <p className="text-text-secondary mb-8">
                  Kontaktieren Sie uns direkt per Telefon oder E-Mail oder besuchen Sie uns an unserem Standort.
                </p>
              </div>

              <div className="space-y-6">
                <a 
                  href="tel:+491775535581"
                  className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-accent/40 hover:bg-white/70 transition-all cursor-pointer block"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-2">Telefon</h3>
                      <p className="text-text-secondary">+49 177 5535581</p>
                      <p className="text-sm text-accent hover:underline mt-1">
                        Jetzt anrufen →
                      </p>
                    </div>
                  </div>
                </a>

                <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-2">E-Mail</h3>
                      <p className="text-text-secondary">info@elysana-massage.de</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-2">Öffnungszeiten</h3>
                      <p className="text-text-secondary">
                        Mo-Fr: 09:00 - 18:00 Uhr<br />
                        Samstag: Nach Vereinbarung
                      </p>
                    </div>
                  </div>
                </div>

                <a 
                  href="https://www.google.com/maps/place/Massage+Elysana/data=!4m2!3m1!1s0x0:0x2a24231e81bbda39?sa=X&ved=1t:2428&hl=de&ictx=111"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-accent/40 hover:bg-white/70 transition-all cursor-pointer block"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-2">Location</h3>
                      <p className="text-text-secondary mb-1">
                        Kölnerstr. 296<br />
                        51645 Gummersbach
                      </p>
                      <p className="text-sm text-accent hover:underline">
                        In Google Maps öffnen →
                      </p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Google Maps */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/20 overflow-hidden"
              >
                <h3 className="text-xl font-bold text-primary mb-4">Finden Sie uns</h3>
                <div className="relative w-full h-[400px] rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2510.8467889427394!2d7.562896776892474!3d51.02767397171795!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x2a24231e81bbda39!2sMassage%20Elysana!5e0!3m2!1sde!2sde!4v1733587200000!5m2!1sde!2sde"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Elysana Massage Location"
                  ></iframe>
                </div>
                <div className="mt-4 text-center">
                  <a
                    href="https://www.google.com/maps/place/Massage+Elysana/data=!4m2!3m1!1s0x0:0x2a24231e81bbda39?sa=X&ved=1t:2428&hl=de&ictx=111"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-accent hover:text-primary transition-colors font-medium"
                  >
                    <MapPin size={18} />
                    Größere Karte in Google Maps anzeigen
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Gallery Carousel Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-24"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Unsere Galerie</h2>
              <p className="text-lg text-text-secondary">Entdecken Sie unsere entspannende Atmosphäre</p>
            </div>

            <div className="relative bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20 overflow-hidden">
              {/* Main Image Display */}
              <div className="relative h-[500px] rounded-xl overflow-hidden mb-6">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={galleryImages[currentImageIndex].url}
                    alt={galleryImages[currentImageIndex].alt}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full object-cover"
                  />
                </AnimatePresence>

                {/* Navigation Arrows */}
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
                  aria-label="Vorheriges Bild"
                >
                  <ChevronLeft className="text-primary" size={24} />
                </button>

                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
                  aria-label="Nächstes Bild"
                >
                  <ChevronRight className="text-primary" size={24} />
                </button>

                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-primary/90 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {currentImageIndex + 1} / {galleryImages.length}
                </div>
              </div>

              {/* Thumbnail Navigation */}
              <div className="flex justify-center gap-3 flex-wrap">
                {galleryImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`relative w-20 h-20 rounded-lg overflow-hidden transition-all ${
                      index === currentImageIndex
                        ? "ring-4 ring-accent scale-110"
                        : "ring-2 ring-gray-300 hover:ring-accent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;